import { useEffect, useState, type FormEvent } from 'react';
import { Button, Input, Label, PageSectionCard, Skeleton, toast } from '@kmgestao/ui';

import {
  PLATFORM_SETTING_FIELDS,
  buildPlatformSettingsFormSchema,
  usePlatformSettingsAdmin,
  useSavePlatformSettings,
  type PlatformSettingField,
  type PlatformSettingsFormInput,
} from '../../../../features/platform-settings-admin/index.js';

import { CollapsibleSectionCard } from './CollapsibleSectionCard.js';
import { ContractClausesSection } from './ContractClausesSection.js';
import { PlatformSettingsPreviewDialog } from './PlatformSettingsPreviewDialog.js';

/** Formulário de edição dos `platform_settings`. Campos agrupados:
 *  - Contratada (9 campos fiscais/comerciais, incluindo endereço+CEP)
 *  - Corpo da proposta (5 textos com placeholders)
 *  - Rodapé institucional (1 textarea)
 *  - Contrato (14 cláusulas editáveis em 3 sub-cards — ver
 *    ContractClausesSection)
 *
 * Validação síncrona (Zod dinâmico) bloqueia salvar com campo
 * obrigatório vazio. */
export function PlatformSettingsForm() {
  const query = usePlatformSettingsAdmin();
  const saveMutation = useSavePlatformSettings();

  const [values, setValues] = useState<PlatformSettingsFormInput>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [previewOpen, setPreviewOpen] = useState(false);

  // Hidrata o state local quando a query terminar. `useEffect` com
  // comparação via JSON.stringify pra não entrar em loop se o
  // react-query devolver uma nova referência sem mudança real de conteúdo.
  useEffect(() => {
    if (query.data) {
      setValues(query.data);
      setErrors({});
    }
  }, [query.data]);

  if (query.isLoading) {
    return (
      <PageSectionCard title="Dados da plataforma" description="Carregando configurações atuais…">
        <div className="space-y-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
        </div>
      </PageSectionCard>
    );
  }

  if (query.isError) {
    return (
      <PageSectionCard title="Dados da plataforma">
        <p className="text-sm text-destructive">
          Erro ao carregar configurações:{' '}
          {query.error instanceof Error ? query.error.message : 'desconhecido'}
        </p>
      </PageSectionCard>
    );
  }

  const handleChange = (key: string) => (value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    // Limpa erro do campo conforme o user digita — evita manter erro
    // antigo depois do fix.
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const schema = buildPlatformSettingsFormSchema();
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path[0];
        if (typeof path === 'string' && !fieldErrors[path]) {
          fieldErrors[path] = issue.message;
        }
      }
      setErrors(fieldErrors);

      // Toast explícito — evita "silent fail" quando o campo com erro
      // está num card colapsado (ex: cláusulas de contrato vazias em
      // ambiente recém-seedado) e o scroll não faz nada visualmente.
      // Bug reportado 2026-04-24: Alexandre clicou "Salvar" e nada
      // aconteceu porque os 12 campos de contrato estavam vazios em
      // cards fechados.
      const errorCount = Object.keys(fieldErrors).length;
      toast.error(
        `Preencha o${errorCount > 1 ? 's' : ''} ${errorCount} campo${errorCount > 1 ? 's' : ''} obrigatório${errorCount > 1 ? 's' : ''} antes de salvar`,
        {
          description:
            'Campos pendentes podem estar em cards recolhidos — expanda as seções pra revisar.',
        },
      );

      // Rola até o primeiro erro pra o user ver imediatamente.
      const first = Object.keys(fieldErrors)[0];
      if (first) {
        const el = document.getElementById(`platform-setting-${first}`);
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }
    try {
      await saveMutation.mutateAsync(parsed.data);
      toast.success('Configurações salvas.');
    } catch (err) {
      toast.error('Não foi possível salvar', {
        description: err instanceof Error ? err.message : 'erro desconhecido',
      });
    }
  };

  const contratadaFields = PLATFORM_SETTING_FIELDS.filter((f) => f.group === 'contratada');
  const footerFields = PLATFORM_SETTING_FIELDS.filter((f) => f.group === 'footer');
  const proposalCopyFields = PLATFORM_SETTING_FIELDS.filter((f) => f.group === 'proposal_copy');

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      <CollapsibleSectionCard
        title="Dados da contratada"
        description="Informações legais/comerciais da KM Gestão exibidas nas propostas comerciais."
      >
        <div className="space-y-5">
          {contratadaFields.map((field) => (
            <FieldRow
              key={field.key}
              field={field}
              value={values[field.key] ?? ''}
              error={errors[field.key]}
              onChange={handleChange(field.key)}
              disabled={saveMutation.isPending}
            />
          ))}
        </div>
      </CollapsibleSectionCard>

      {/* Ordem das seções acompanha o fluxo de leitura da proposta
          pelo lead: (1) Contratada aparece implicitamente em header/
          footer; (2) Corpo da proposta — blocos 01, 02, Considerações;
          (3) Rodapé institucional — última linha da proposta. Alexandre
          reclamou da ordem antiga (rodapé antes do corpo) em 2026-04-24. */}
      <CollapsibleSectionCard
        title="Textos do corpo da proposta"
        description="Blocos 01 (abertura), 02 (Sobre nós) e bullets estáticos de Considerações. Alterações refletem em novas visualizações de proposta, incluindo links que já foram enviados."
      >
        <div className="mb-4 rounded-md border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
          <p className="font-semibold text-foreground">Placeholders disponíveis:</p>
          <ul className="mt-1 space-y-0.5">
            <li>
              <code className="rounded bg-background px-1 py-0.5">{'{firstName}'}</code> — primeiro
              nome do contato na clínica
            </li>
            <li>
              <code className="rounded bg-background px-1 py-0.5">{'{destinatario}'}</code> —
              clínica / Dr. (nome do destinatário da proposta)
            </li>
            <li>
              <code className="rounded bg-background px-1 py-0.5">{'{razao_social}'}</code> e{' '}
              <code className="rounded bg-background px-1 py-0.5">{'{nome_fantasia}'}</code> — dados
              da contratada (seção acima)
            </li>
            <li>
              <code className="rounded bg-background px-1 py-0.5">
                {'{consulting_duration_months}'}
              </code>{' '}
              e <code className="rounded bg-background px-1 py-0.5">{'{saas_cobranca_note}'}</code>{' '}
              — só no bullet pós-consultoria
            </li>
          </ul>
        </div>
        <div className="space-y-5">
          {proposalCopyFields.map((field) => (
            <FieldRow
              key={field.key}
              field={field}
              value={values[field.key] ?? ''}
              error={errors[field.key]}
              onChange={handleChange(field.key)}
              disabled={saveMutation.isPending}
            />
          ))}
        </div>
      </CollapsibleSectionCard>

      <CollapsibleSectionCard
        title="Texto institucional do rodapé"
        description="Frase de fechamento que aparece no final de TODAS as propostas públicas."
      >
        <div className="space-y-5">
          {footerFields.map((field) => (
            <FieldRow
              key={field.key}
              field={field}
              value={values[field.key] ?? ''}
              error={errors[field.key]}
              onChange={handleChange(field.key)}
              disabled={saveMutation.isPending}
            />
          ))}
        </div>
      </CollapsibleSectionCard>

      {/* Cláusulas do contrato — 3 sub-cards colapsáveis (Objeto, Valor,
          Cláusulas 3-8). Delegado a um componente dedicado pra não
          inflar este arquivo (hard limit 300 linhas). */}
      <ContractClausesSection
        fields={PLATFORM_SETTING_FIELDS}
        values={values}
        errors={errors}
        onChange={handleChange}
        disabled={saveMutation.isPending}
      />

      {/* Barra de ações — prévia + salvar. Separada dos cards porque
          aplica a TODOS os settings (não só a uma seção). Antes o botão
          de prévia estava dentro do card de Corpo da proposta, o que
          dava a impressão de que só previsualizava aqueles campos. */}
      <div className="flex flex-col gap-3 border-t border-border/50 pt-6 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">
          Veja como a proposta vai ficar pro lead usando os valores atuais do formulário (inclusive
          edições não-salvas).
        </p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => setPreviewOpen(true)}>
            Ver prévia da proposta
          </Button>
          <Button type="submit" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Salvando…' : 'Salvar alterações'}
          </Button>
        </div>
      </div>

      <PlatformSettingsPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        formValues={values}
      />
    </form>
  );
}

function FieldRow({
  field,
  value,
  error,
  onChange,
  disabled,
}: {
  field: PlatformSettingField;
  value: string;
  error?: string | undefined;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  const id = `platform-setting-${field.key}`;
  const describedBy = error ? `${id}-error` : `${id}-help`;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {field.label}
        {field.required ? <span className="ml-1 text-destructive">*</span> : null}
      </Label>
      {field.input === 'textarea' ? (
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={5}
          placeholder={field.placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        />
      ) : (
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={field.placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
        />
      )}
      {error ? (
        <p id={`${id}-error`} className="text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : (
        <p id={`${id}-help`} className="text-xs text-muted-foreground">
          {field.helpText}
        </p>
      )}
    </div>
  );
}
